import { Injectable, OnDestroy, Optional, SkipSelf } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { isEqual } from 'lodash-es';
import { LanguageCode } from '../entities/language-code.entity';
import { LanguageLocalePair } from '../entities/language-locale-pair.entity';
import { LocaleCode } from '../entities/locale-code.entity';

@Injectable({ providedIn: 'root' })
export class SharedLanguageService implements OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private readonly HARDCODED_FALLBACK: LanguageLocalePair = {
        Language: 'nl' as LanguageCode,
        Locale: 'nl_BE' as LocaleCode
    };

    private environmentFallbackLocale: LocaleCode | undefined;

    private supportedLanguageLocalePairs$: Observable<LanguageLocalePair[]> = of([this.HARDCODED_FALLBACK]);

    private langChange$!: Observable<LanguageLocalePair>;
    private currentLocale!: LocaleCode;
    private currentLanguage!: LanguageCode;

    constructor(
        protected translateService: TranslateService,
        @Optional() @SkipSelf() existing?: SharedLanguageService
    ) {
        if (existing) {
            return existing;
        }

        return this;
    }

    public init(fallbackLocale: LocaleCode | undefined): Promise<any> {
        this.environmentFallbackLocale = fallbackLocale;
        this.initLangChangedStream();
        this.initDefaultLangListener();
        this.initLangChangeListener();

        return this.getDefaultLang().pipe(take(1)).toPromise();
    }

    public initSupportedLanguagesAndPairs(supportedLocales: LanguageLocalePair[] | null | undefined): void {
        this.supportedLanguageLocalePairs$ = of(supportedLocales).pipe(
            map(pairs => (!pairs || pairs.length === 0 ? [this.HARDCODED_FALLBACK] : pairs))
        );
    }

    public changeLocale(locale: string): void {
        this.findSupportedLanguageLocalePair(locale)
            .pipe(
                switchMap(result => (result ? of(result) : this.getDefaultLang())),
                take(1),
                takeUntil(this.destroy$)
            )
            .subscribe(languagePair => this.onLangChange(languagePair));
    }

    private initLangChangedStream(): void {
        this.langChange$ = this.translateService.onLangChange.pipe(
            map(langChangeEvent => langChangeEvent.lang),
            distinctUntilChanged(),
            switchMap(language => this.findSupportedLanguageLocalePair(language)),
            switchMap(result => (result ? of(result) : this.getDefaultLang())),
            distinctUntilChanged(isEqual)
        );
    }

    private initDefaultLangListener(): void {
        this.getDefaultLang()
            .pipe(takeUntil(this.destroy$))
            .subscribe(defaultLang => this.translateService.setDefaultLang(defaultLang.Locale));
    }

    public getCurrentLanguage(): LanguageCode {
        return this.currentLanguage ?? this.HARDCODED_FALLBACK.Language;
    }

    private initLangChangeListener(): void {
        this.langChange$.pipe(takeUntil(this.destroy$)).subscribe(languagePair => this.onLangChange(languagePair));
    }

    private onLangChange(languagePair: LanguageLocalePair) {
        this.currentLocale = languagePair.Locale;
        this.currentLanguage = languagePair.Language;
        this.translateService.use(languagePair.Locale);
    }

    public getCurrentLocale(): LocaleCode {
        return this.currentLocale ?? this.HARDCODED_FALLBACK.Locale;
    }

    public translate(token: string, params?: object): string {
        return this.translateService.instant(token, params);
    }

    public getDefaultLang(): Observable<LanguageLocalePair> {
        return this.supportedLanguageLocalePairs$.pipe(
            switchMap(languagePairs =>
                this.environmentFallbackLocale && this.isLocaleSupported(this.environmentFallbackLocale)
                    ? of(languagePairs.find(pair => pair.Locale === this.environmentFallbackLocale))
                    : of(this.HARDCODED_FALLBACK)
            ),
            map(fallbackLanguage => (fallbackLanguage ? fallbackLanguage : this.HARDCODED_FALLBACK)),
            take(1),
            shareReplay()
        );
    }

    public getSupportedLanguageLocalePairs(): Observable<LanguageLocalePair[]> {
        return this.supportedLanguageLocalePairs$;
    }

    private isLocaleSupported(locale: string): Observable<boolean> {
        return this.findSupportedLanguageLocalePair(locale).pipe(map(result => !!result));
    }

    private findSupportedLanguageLocalePair(locale: string): Observable<LanguageLocalePair | undefined> {
        return this.supportedLanguageLocalePairs$.pipe(
            map(supportedLanguages =>
                supportedLanguages.find(pair => pair.Locale.toLowerCase() === locale.toLowerCase())
            ),
            tap(isSupported => {
                if (!isSupported) {
                    console.warn(`Locale "${locale}" is currently not supported`);
                }
            })
        );
    }

    // fallback
    public instant(token: string, params?: object | undefined): string {
        return this.translateService.instant(token, params);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
